import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { apiGetMfaStatusOptions } from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { errorText } from "../../../app/error-text";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { useDialog } from "../../../components/ui-kit/dialog-provider";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { Spinner } from "../../../components/ui-kit/spinner";
import { BackupCodes } from "./backup-codes";
import { MfaEnrollment } from "./mfa-enrollment";

/** Which dialog, if any, is open over the settings rows. */
type OpenDialog =
  | { kind: "none" }
  /** `replacing` only changes the wording — the endpoints are the same. */
  | { kind: "enroll"; replacing: boolean }
  | { kind: "backupCodes"; codes: string[] };

/**
 * The two-factor rows on the account page: what is set up, and the two things
 * a user can do about it. Every write here is gated on a fresh token, so the
 * password prompt is a normal part of the flow rather than an edge case.
 */
export function MfaSettings({ api }: { api: ApiConnector }) {
  const {
    data: status,
    error: statusError,
    refetch,
  } = useQuery(apiGetMfaStatusOptions());
  const [dialog, setDialog] = useState<OpenDialog>({ kind: "none" });
  const { t } = useLingui();
  const { confirm } = useDialog();

  async function regenerateBackupCodes() {
    const { backup_codes } = await api.regenerateBackupCodes();
    setDialog({ kind: "backupCodes", codes: backup_codes });
    // The old codes are dead now, so the count has to come from the server.
    await refetch();
  }

  async function removeTotp() {
    const confirmed = await confirm(
      t`Your backup codes will be deleted with it. You can set two-factor authentication up again at any time.`,
      t`Remove your authenticator app?`,
      { confirmLabel: t`Remove` },
    );

    if (!confirmed) {
      return;
    }

    await api.removeTotp();
    await refetch();
  }

  if (!status) {
    return (
      <FormRow
        label={<Trans>Two-factor authentication</Trans>}
        data={
          statusError ? (
            <ErrorMessage message={errorText(statusError)} />
          ) : (
            <Spinner />
          )
        }
      />
    );
  }

  return (
    <>
      <FormRow
        label={<Trans>Authenticator app</Trans>}
        hint={
          status.required && !status.totp_enrolled ? (
            <Trans>Required for your account</Trans>
          ) : undefined
        }
        data={
          status.totp_enrolled ? (
            <div className="flex items-center gap-3">
              <span>
                <Trans>Enabled</Trans>
              </span>
              {/* Enrolling again overwrites the secret — how a user moves to a
                  new phone, or recovers from losing one. */}
              <Button
                variant="subtle"
                onClick={() => {
                  setDialog({ kind: "enroll", replacing: true });
                }}
              >
                <Trans>Replace</Trans>
              </Button>
              {/* The backend refuses to take away the last method on an account
                  two-factor is required for, and today this is always it. */}
              {!status.required && (
                <Button variant="subtle" onClick={removeTotp}>
                  <Trans>Remove</Trans>
                </Button>
              )}
            </div>
          ) : (
            <Button
              onClick={() => {
                setDialog({ kind: "enroll", replacing: false });
              }}
            >
              <Trans>Set up</Trans>
            </Button>
          )
        }
      />

      {status.totp_enrolled && (
        <FormRow
          label={<Trans>Backup codes</Trans>}
          data={
            <div className="flex items-center gap-3">
              <span>
                <Trans>{status.backup_codes_remaining} remaining</Trans>
              </span>
              <Button variant="subtle" onClick={regenerateBackupCodes}>
                <Trans>Regenerate</Trans>
              </Button>
            </div>
          }
        />
      )}

      {dialog.kind === "enroll" && (
        // Not dismissible by backdrop or Escape: leaving discards the pending
        // secret, so a stray click would kill an entry the user may already
        // have scanned. Cancel is the only way out.
        <Dialog open>
          {/* A floor, not a fixed width: the QR code sets this, but the
              backup codes shown at the end of enrolling are wider. */}
          <div className="min-w-96">
            <MfaEnrollment
              api={api}
              intro={
                dialog.replacing ? (
                  <Trans>
                    Scan this QR code with your new authenticator app, then
                    enter the code it shows. The old one stops working as soon
                    as this is done.
                  </Trans>
                ) : (
                  <Trans>
                    Scan this QR code with an authenticator app, then enter the
                    code it shows.
                  </Trans>
                )
              }
              expiredMessage={
                <Trans>Setup expired before it was finished.</Trans>
              }
              onEnrolled={() => {
                setDialog({ kind: "none" });
                void refetch();
              }}
              onCancel={() => {
                setDialog({ kind: "none" });
              }}
              cancelLabel={<Trans>Cancel</Trans>}
            />
          </div>
        </Dialog>
      )}

      {dialog.kind === "backupCodes" && (
        <Dialog open>
          <BackupCodes
            codes={dialog.codes}
            onContinue={() => {
              setDialog({ kind: "none" });
            }}
          />
        </Dialog>
      )}
    </>
  );
}
